/* eslint-disable react-hooks/exhaustive-deps */
import {
    Avatar,
    Col,
    Divider,
    Flex,
    Form,
    Input,
    InputNumber,
    Modal,
    Row,
    Select,
    Skeleton,
    Space,
    Spin,
    Typography,
    Upload,
} from 'antd';
import { useCallback, useEffect, useState } from 'react';
import {
    addCustomerSuggestedPrice,
    updateCustomerSuggestedPrice,
} from '../../../../../../redux/actions/customers';
import { getProductDetails, getProducts } from '../../../../../../redux/actions/products';
import { formatCurrency, getProductImageUrl, sortPriceLevels } from '../../../../../../utils';
import { InboxOutlined, LoadingOutlined } from '@ant-design/icons';
import {
    extractOriginalFileName,
    getFileUrl,
    getNewFileName,
    getUploadUrl,
    uploadFileToS3,
} from '../../../../../../redux/actions/upload';

const { Dragger } = Upload;
const { Text } = Typography;

const AddSuggestedPrice = ({ open, onClose, customer, data, afterDataUpdate }) => {
    console.log('🚀 ~ AddSuggestedPrice ~ data:', data);
    const [form] = Form.useForm();

    const [products, setProducts] = useState([]);
    const [priceLevels, setPriceLevels] = useState(null);
    const [files, setFiles] = useState([]);
    const [fileList, setFileList] = useState([]);

    const [isLoadingPriceLevels, setIsLoadingPriceLevels] = useState(false);
    const [isUploadingFile, setIsUploadingFile] = useState(false);

    const selectedProductId = Form.useWatch('product_netsuite_id', form);
    const selectedPriceLevel = Form.useWatch('price_level', form);

    const productImage = !!priceLevels?.length && getProductImageUrl(priceLevels[0]);

    const fetchProducts = async () => {
        const res = await getProducts();
        if (res?.ack) {
            setProducts(res.products);
        }
    };

    const fetchProductPriceLevels = useCallback(
        async (productId) => {
            form.resetFields(['price_level', 'price', 'notes']);

            try {
                setIsLoadingPriceLevels(true);
                const res = await getProductDetails({ id: productId, details: 1, loading: false });

                if (data) {
                    form.setFieldsValue({
                        price: data?.price,
                        price_level: data?.price_level,
                    });

                    const parsedFiles = data?.files && JSON.parse(data.files);
                    if (parsedFiles) {
                        const formattedFileList = parsedFiles.map((file) => ({
                            name: extractOriginalFileName(file),
                            url: getFileUrl('custom-prices', customer?.customer_netsuite_id, file),
                            status: 'done',
                        }));
                        setFileList(formattedFileList);
                        setFiles(parsedFiles);
                    }
                }

                if (res?.ack) {
                    const sortedPriceLevels = sortPriceLevels(res?.product);
                    setPriceLevels(sortedPriceLevels);
                }
            } finally {
                setIsLoadingPriceLevels(false);
            }
        },
        [form, data, customer]
    );

    const handlePriceLevelChange = (value) => {
        if (value !== 'Custom') {
            const selectedTier = priceLevels.find((price) => price.price_level === value);
            form.setFieldsValue({ price: selectedTier?.unit_price });
        } else {
            form.setFieldsValue({ price: null });
        }
    };

    const handleFileUpload = async ({ file }) => {
        if (file.status === 'removed' || !file) return;

        setIsUploadingFile(true);

        try {
            const fileName = getNewFileName(file.name);
            const uploadUrl = await getUploadUrl({
                file,
                module: 'custom-prices',
                recordID: customer?.customer_netsuite_id,
                display_name: fileName,
            });

            await uploadFileToS3(uploadUrl, file, file.type);

            const fileUrl = getFileUrl('custom-prices', customer?.customer_netsuite_id, fileName);

            setFiles((prev) => [...(prev || []), fileName]);
            setFileList((prev) => [
                ...(Array.isArray(prev) ? prev : []),
                {
                    name: file.name,
                    url: fileUrl,
                    status: 'done',
                },
            ]);
        } catch (error) {
            console.error('Error uploading file:', error);
        } finally {
            setIsUploadingFile(false);
        }
    };

    const handleFileRemove = (file) => {
        const fileIndex = fileList.findIndex((item) => item.name === file.name);
        if (fileIndex === -1) return false;

        setFileList((prev) => prev.filter((_, i) => i !== fileIndex));
        setFiles((prev) => prev.filter((_, i) => i !== fileIndex));

        return true;
    };

    const handleFormSubmit = async (values) => {
        const requestBody = {
            ...values,
            notes: values?.notes || '',
            customer_netsuite_id: customer?.customer_netsuite_id,
            customer_code: customer?.code,
            customer_name: customer?.title,
            product_code: priceLevels[0]?.code,
            product_name: priceLevels[0]?.name,
            files: files,
        };

        if (data) requestBody.id = data?.id;

        const res = data
            ? await updateCustomerSuggestedPrice(requestBody)
            : await addCustomerSuggestedPrice(requestBody);

        if (res?.ack) {
            onClose();
            afterDataUpdate?.();
        }
    };

    useEffect(() => {
        fetchProducts();

        if (data) {
            fetchProductPriceLevels(data?.netsuite_id || data?.product_netsuite_id);
            form.setFieldsValue({
                ...data,
                product_netsuite_id: data?.netsuite_id || data?.product_netsuite_id,
            });
        }
    }, [data]);

    const renderFormFieldSkeleton = () => (
        <Row gutter={16} className="mt-4">
            <Col span={12}>
                <div className="mb-3">
                    <Skeleton.Input
                        active
                        block
                        size="small"
                        style={{ width: 80, height: 22, marginBottom: 8 }}
                    />
                    <Skeleton.Input active block style={{ height: 40 }} />
                </div>
            </Col>

            <Col span={12}>
                <div className="mb-3">
                    <Skeleton.Input
                        active
                        block
                        size="small"
                        style={{ width: 50, height: 22, marginBottom: 8 }}
                    />
                    <Skeleton.Input active block style={{ height: 40 }} />
                </div>
            </Col>

            <Col span={24}>
                <div className="mb-3">
                    <Skeleton.Input
                        active
                        block
                        size="small"
                        style={{ width: 60, height: 22, marginBottom: 8 }}
                    />
                    <Skeleton.Input active block style={{ height: 64 }} />
                </div>
            </Col>

            <Col span={24} className="w-full">
                <Skeleton.Node active className="w-full h-10rem">
                    <Flex vertical align="center" justify="center">
                        <InboxOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />
                    </Flex>
                </Skeleton.Node>
            </Col>
        </Row>
    );

    return (
        <Modal
            title={
                <Space size={8} split={<Divider className='m-0' type="vertical" style={{ borderColor: '#d9d9d9' }} />}>
                    {customer ? `[${customer.code}] ${customer?.title ?? ''}` : 'Suggested Price'}
                    {data?.created_by && (
                        <Text type="secondary">
                            Requested by: <Text strong>{data.created_by}</Text>
                        </Text>
                    )}
                </Space>
            }
            open={open}
            onCancel={onClose}
            centered
            width={800}
            okText={data ? 'Update' : 'Save'}
            onOk={() => form.submit()}
        >

            <Form
                layout="vertical"
                form={form}
                name="suggested_price"
                size="large"
                className="tight-form"
                onFinish={handleFormSubmit}
            >
                {selectedProductId && productImage && (
                    <Avatar shape="square" size={100} src={productImage} className="mb-3" />
                )}

                <Form.Item
                    name="product_netsuite_id"
                    label="Product"
                    rules={[{ required: true, message: 'Please select a product' }]}
                >
                    <Select
                        placeholder="Search Product"
                        optionLabelProp="label"
                        showSearch
                        filterOption={(input, option) =>
                            String(option['data-title'] || '')
                                .toLowerCase()
                                .includes(input.toLowerCase()) ||
                            String(option['data-code'] || '')
                                .toLowerCase()
                                .includes(input.toLowerCase())
                        }
                        onChange={fetchProductPriceLevels}
                        disabled={!!data}
                    >
                        {products?.map((product, idx) => (
                            <Select.Option
                                key={idx}
                                value={product?.netsuite_id || product?.product_netsuite_id}
                                label={`[${product?.code}] ${product?.name}`}
                                data-code={product?.code}
                                data-title={product?.name}
                            >
                                <Text>
                                    [{product?.code}] {product?.name}
                                </Text>
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>

                {selectedProductId &&
                    (isLoadingPriceLevels ? (
                        renderFormFieldSkeleton()
                    ) : (
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="price_level"
                                    label="Price Level"
                                    rules={[{ required: true, message: 'Please select a price level' }]}
                                >
                                    <Select
                                        placeholder="Select Price Level"
                                        onChange={handlePriceLevelChange}
                                        optionLabelProp="label"
                                    >
                                        {priceLevels?.map((priceLevel, idx) => (
                                            <Select.Option
                                                key={idx}
                                                value={priceLevel.price_level}
                                                label={priceLevel.price_level}
                                            >
                                                <Text>
                                                    {priceLevel.price_level} - {formatCurrency(priceLevel?.unit_price)}
                                                </Text>
                                            </Select.Option>
                                        ))}
                                        <Select.Option value="Custom" label="Custom Price">
                                            Custom Price
                                        </Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Col span={12}>
                                <Form.Item
                                    name="price"
                                    label="Price"
                                    rules={[
                                        {
                                            required: selectedPriceLevel === 'Custom',
                                            message: 'Please enter price',
                                        },
                                    ]}
                                >
                                    <InputNumber
                                        style={{ width: '100%' }}
                                        min={0}
                                        controls={false}
                                        readOnly={selectedPriceLevel !== 'Custom'}
                                        prefix="£"
                                        placeholder="Enter price"
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={24}>
                                <Form.Item name="notes" label="Notes">
                                    <Input.TextArea rows={2} placeholder="Add any additional notes..." />
                                </Form.Item>
                            </Col>

                            <Col span={24}>
                                <Form.Item>
                                    <Dragger
                                        name="file"
                                        multiple={false}
                                        accept="*"
                                        listType="picture"
                                        fileList={fileList}
                                        onChange={handleFileUpload}
                                        onRemove={handleFileRemove}
                                        beforeUpload={() => false}
                                    >
                                        <Flex vertical align="center" justify="center">
                                            {isUploadingFile ? (
                                                <Flex vertical align="center" gap={8}>
                                                    <Spin indicator={<LoadingOutlined spin />} size="large" />
                                                    <Text type="secondary">Uploading your file...</Text>
                                                </Flex>
                                            ) : (
                                                <>
                                                    <InboxOutlined className="text-5xl" />
                                                    <Text strong>Click or drag file to this area to upload</Text>
                                                </>
                                            )}
                                        </Flex>

                                        <Text type="secondary" className="max-w-22rem flex m-auto mt-2">
                                            Support for a single upload. Strictly prohibit from uploading company data or
                                            other banned files.
                                        </Text>
                                    </Dragger>
                                </Form.Item>
                            </Col>
                        </Row>
                    ))}
            </Form>
        </Modal>
    );
};

export default AddSuggestedPrice;
